import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { PawIcon, SearchIcon } from "@fawkes/ui";

import { speciesIconSrc } from "../components/AnimalOverview";
import { useModuleAvailability } from "../api/modules";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { formatDate } from "../i18n/format";
import "./NazorgBeheer.css";
import { NazorgHuisstijlTab } from "./NazorgHuisstijl";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// ── wire types (GET /api/nazorg/beheer) ────────────────────────────────────────────────────────────
// Het scherm heeft nog drie tabbladen: Overzicht (alle binnengekomen nazorg, met dezelfde opbouw en
// filters als het overzicht Aanwezig), Taken (wat er nog uitgaat en wat al verstuurd is) en als laatste
// Vragenlijsten. De inbox "Antwoorden & signalen" en het mock-tabblad "Adoptant-portaal" zijn eruit:
// het eerste zei hetzelfde als het overzicht, het tweede was een plaatje van een telefoon.
interface Resultaat {
  /** De inzending achter de kaart: doorklikken opent het nazorgdossier, niet het dier. */
  inzendingId: number | null;
  dierId: number | null;
  naam: string | null;
  notitie: string | null;
  prioriteit: string | null;
  fotoUrl: string | null;
  diersoort: string | null;
  adoptant: string | null;
  vragenlijst: string | null;
  contactmoment: string | null;
  contactmomentLabel: string | null;
  /** Het hoeveelste nazorgmoment bij dit dier; meestal 1. */
  momentNummer: number | null;
  opgepakt: boolean;
  ingediendOp: string | null;
  geleden: string | null;
  signalen: number;
}
interface Overzicht {
  lopend: number;
  rodeVlaggen: number;
  teVolgen: number;
  huisbezoeken: number;
  reactiegraad: number;
  resultaten: Resultaat[];
}
interface Vragenlijst {
  id: string;
  naam: string | null;
  diersoort: string | null;
  contactmoment: string | null;
  contactmomentLabel: string | null;
  kanaal: string | null;
  status: string | null;
}
interface NazorgPayload {
  overzicht: Overzicht;
  vragenlijsten: Vragenlijst[];
  diersoorten: string[];
}

/** Eén regel op de Taken-tab (GET /api/nazorg/beheer/verzendingen). */
interface Verzending {
  uitnodigingId: number | null;
  dierId: number | null;
  dier: string | null;
  fotoUrl: string | null;
  diersoort: string | null;
  adoptant: string | null;
  adoptantPlaats: string | null;
  adoptantContact: string | null;
  vragenlijst: string | null;
  contactmoment: string | null;
  contactmomentLabel: string | null;
  volgnummer: number;
  datum: string | null;
  status: string;
  inzendingId: number | null;
  /** Het nazorgdossier dat deze regel opent; leeg als dit dier nog nooit iets invulde. */
  dossierInzendingId: number | null;
}

type TabKey = "overzicht" | "taken" | "vragenlijsten" | "huisstijl";
// Vragenlijsten en Lay-out en thema staan achteraan: dat is inrichtingswerk, geen dagelijkse lijst.
const TABS: TabKey[] = ["overzicht", "taken", "vragenlijsten", "huisstijl"];

export interface NazorgBeheerProps {
  /**
   * Open het nazorgdossier van één inzending. Zo gevraagd: een nazorgtegel hoort de antwoorden te
   * openen, niet het dierscherm.
   */
  onOpenInzending: (inzendingId: string) => void;
  // nazorg-authoring epic: questionnaire authoring lives in the in-SPA wizard-builder
  // (Instellingen → Registraties & Wizards). These callbacks navigate there in-SPA — a fresh
  // nazorg-vragenlijst, or the builder opened on an existing definition.
  /** Create a new nazorg-vragenlijst and open it in the settings wizard-builder. */
  onNieuweVragenlijst: () => void;
  /** Open an existing vragenlijst (wizard-definition id) in the settings wizard-builder. */
  onOpenVragenlijst: (definitionId: string) => void;
  /**
   * Op welk tabblad dit scherm opengaat.
   *
   * <p>Nodig om terug te kunnen keren uit de vragenlijstbouwer: je klikte daar vanaf het tabblad
   * Vragenlijsten op, en op het overzicht terugkomen is niet hetzelfde als terug.
   */
  initialTab?: TabKey;
}

/** Lay-out en thema: de huisstijl van het asiel, met een levend voorbeeld. */
function initial(naam: string | null): string {
  return (naam || "?").trim().charAt(0).toUpperCase() || "?";
}

// ── Nazorg-beheer route — the backoffice aftercare admin screen (Jmix NazorgBeheerView migration). Read
// payload from GET /api/nazorg/beheer; de verzendlijst komt van GET /api/nazorg/beheer/verzendingen.
// Vragenlijsten authoring hands off in-SPA to the settings wizard-builder (nazorg-authoring).
export function NazorgBeheer({ onOpenInzending, onNieuweVragenlijst, onOpenVragenlijst,
                              initialTab }: NazorgBeheerProps) {
  const t = useTranslate();
  const k = (suffix: string) => t(`nazorg.${suffix}`);

  // Modulebeheer gate: defense-in-depth behind App.tsx's nav filter + deep-link guard (both keyed on
  // the nav item's moduleKey "nazorg") — the page also hides itself when the module is toggled off,
  // so it stays safe even if rendered outside those guards. Permissive while the feed loads.
  const { isModuleEnabled } = useModuleAvailability();
  const moduleEnabled = isModuleEnabled("nazorg");

  const [tab, setTab] = useState<TabKey>(initialTab ?? "overzicht");

  const { data: resp, isLoading } = useCustom<NazorgPayload>({
    url: "nazorg/beheer",
    method: "get",
    queryOptions: { enabled: moduleEnabled },
  });
  const data = resp?.data ?? null;
  const loading = isLoading || !data;

  /** Doorklikken opent altijd de nazorg; dit scherm brengt je nergens meer naar het dierscherm. */
  const openInzending = (inzendingId: number | null) => {
    if (inzendingId != null) onOpenInzending(String(inzendingId));
  };

  const rodeVlaggen = data?.overzicht?.rodeVlaggen ?? 0;

  const tabBadge = (tk: TabKey): number | null => {
    if (!data) return null;
    if (tk === "vragenlijsten") return data.vragenlijsten.length || null;
    if (tk === "overzicht") return rodeVlaggen || null;
    return null;
  };

  // The hook is permissive while loading; only an explicit "disabled" hides the screen.
  if (!moduleEnabled) {
    return (
      <div className="nz">
        <div className="empty">{k("empty.moduleDisabled")}</div>
      </div>
    );
  }

  return (
    <div className="nz da-overview">
      {/* De vijf kerncijfers staan in de kop, klein — je wilt ze zien, niet erlangs scrollen. */}
      <div className="da-overview__header">
        <div className="da-overview__intro">
          <span className="da-overview__icon" aria-hidden="true"><PawIcon width={30} height={30} /></span>
          <div>
            <h1 className="da-overview__title">{k("title")}</h1>
            <p className="da-overview__subtitle">{k("subtitle")}</p>
          </div>
        </div>
        {data && (
          <div className="nz__cijfers">
            <Cijfer waarde={data.overzicht.lopend} label={k("stat.lopend")} />
            <Cijfer waarde={data.overzicht.rodeVlaggen} label={k("stat.rodeVlaggen")} toon="rood" />
            <Cijfer waarde={data.overzicht.teVolgen} label={k("stat.teVolgen")} toon="oranje" />
            <Cijfer waarde={data.overzicht.huisbezoeken} label={k("stat.huisbezoeken")} />
            <Cijfer waarde={`${data.overzicht.reactiegraad}%`} label={k("stat.reactiegraad")} />
          </div>
        )}
      </div>

      <nav className="nz__tabbar">
        {TABS.map((tk) => {
          const badge = tabBadge(tk);
          const rood = tk === "overzicht" && rodeVlaggen > 0;
          return (
            <button
              key={tk}
              type="button"
              className={"nz__tab" + (tk === tab ? " is-active" : "")}
              onClick={() => setTab(tk)}
            >
              {k(`tab.${tk}`)}
              {badge != null && <span className={"nz__tab-badge" + (rood ? " is-red" : "")}>{badge}</span>}
            </button>
          );
        })}
      </nav>

      <div className="nz__body">
        {tab === "overzicht" && <OverzichtTab k={k} loading={loading} data={data} onOpenInzending={openInzending} />}
        {tab === "taken" && <VerzendingenTab k={k} onOpenInzending={openInzending} />}
        {tab === "vragenlijsten" && (
          <VragenlijstenTab
            k={k}
            loading={loading}
            data={data}
            onNieuw={onNieuweVragenlijst}
            onOpen={onOpenVragenlijst}
          />
        )}
        {tab === "huisstijl" && <NazorgHuisstijlTab k={k} />}
      </div>
    </div>
  );
}

type TFn = (suffix: string) => string;

function PrioPill({ k, prioriteit }: { k: TFn; prioriteit: string | null }) {
  const p = prioriteit || "groen";
  return <span className={"pill pill--" + p}>{k(`prio.${p}`)}</span>;
}

/** Eén kerncijfer in de kop: klein, één regel, geen kaart zo groot als een tegel. */
function Cijfer({ waarde, label, toon }: { waarde: string | number; label: string; toon?: "rood" | "oranje" }) {
  return (
    <div className="nz__cijfer">
      <span className={"nz__cijfer-num" + (toon ? " is-" + toon : "")}>{waarde}</span>
      <span className="nz__cijfer-label">{label}</span>
    </div>
  );
}

/**
 * De diersoort-keuze zoals op Aanwezig: een tegel per soort met zijn plaatje en zijn aantal, en één
 * tegel voor alles samen. Losse tekstpillen zeiden niet hoeveel er achter zaten.
 */
function SoortTegels({
  tegels,
  gekozen,
  onKies,
  alleLabel,
  totaal,
}: {
  tegels: { naam: string; aantal: number }[];
  gekozen: string | null;
  onKies: (soort: string | null) => void;
  alleLabel: string;
  totaal: number;
}) {
  return (
    <div className="da-overview__stats">
      {tegels.map((s) => (
        <button
          key={s.naam}
          type="button"
          className={"da-overview__stat" + (gekozen === s.naam ? " da-overview__stat--active" : "")}
          onClick={() => onKies(gekozen === s.naam ? null : s.naam)}
        >
          <span className="da-overview__stat-icon">
            <img src={speciesIconSrc(s.naam)} alt="" />
          </span>
          <span className="da-overview__stat-body">
            <span className="da-overview__stat-value">{s.aantal}</span>
            <span className="da-overview__stat-label">{s.naam}</span>
          </span>
        </button>
      ))}
      <button
        type="button"
        className={"da-overview__stat" + (gekozen === null ? " da-overview__stat--active" : "")}
        onClick={() => onKies(null)}
      >
        <span className="da-overview__stat-icon da-overview__stat-icon--total" aria-hidden="true">
          <PawIcon width={22} height={22} />
        </span>
        <span className="da-overview__stat-body">
          <span className="da-overview__stat-value">{totaal}</span>
          <span className="da-overview__stat-label">{alleLabel}</span>
        </span>
      </button>
    </div>
  );
}

/** Tel per diersoort, aflopend — de volgorde van de tegels op Aanwezig. */
function soortTegelsUit(soorten: (string | null)[]): { naam: string; aantal: number }[] {
  const per = new Map<string, number>();
  for (const s of soorten) {
    if (!s || !s.trim()) continue;
    per.set(s, (per.get(s) ?? 0) + 1);
  }
  return [...per.entries()]
    .map(([naam, aantal]) => ({ naam, aantal }))
    .sort((a, b) => b.aantal - a.aantal || a.naam.localeCompare(b.naam));
}

const CONTACTMOMENTEN = ["DAG_1_2", "WEEK_1_2", "MAAND_1", "MAANDEN_6"] as const;

// ── Tab 1: Overzicht — dezelfde opbouw als Aanwezig: kerncijfers, soorttegels, een filterbalk en dan
// de kaarten. Alles wat binnenkwam staat erin; de filters maken er een werklijst van.
function OverzichtTab({
  k,
  loading,
  data,
  onOpenInzending,
}: {
  k: TFn;
  loading: boolean;
  data: NazorgPayload | null;
  onOpenInzending: (inzendingId: number | null) => void;
}) {
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState<string | null>(null);
  const [prioriteit, setPrioriteit] = useState("");
  const [moment, setMoment] = useState("");
  // Begint op "open": een gesloten dossier hoort uit de werklijst te verdwijnen. Je vindt het terug
  // via de nazorgtegel bij het verblijf, en wie het hier wil zien zet het filter op gesloten of alle.
  const [afhandeling, setAfhandeling] = useState("open");

  const resultaten = useMemo(() => data?.overzicht?.resultaten ?? [], [data]);
  const tegels = useMemo(() => soortTegelsUit(resultaten.map((r) => r.diersoort)), [resultaten]);

  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return resultaten.filter((r) => {
      if (soort && r.diersoort !== soort) return false;
      if (prioriteit && r.prioriteit !== prioriteit) return false;
      if (moment && r.contactmoment !== moment) return false;
      if (afhandeling === "open" && r.opgepakt) return false;
      if (afhandeling === "opgepakt" && !r.opgepakt) return false;
      if (!term) return true;
      return [r.naam, r.adoptant, r.vragenlijst, r.notitie]
        .some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [resultaten, zoek, soort, prioriteit, moment, afhandeling]);

  const filterActief = !!(zoek || soort || prioriteit || moment || afhandeling !== "open");
  const wis = () => {
    setZoek("");
    setSoort(null);
    setPrioriteit("");
    setMoment("");
    setAfhandeling("");
  };

  if (loading || !data) {
    return (
      <>
        <div className="statgrid">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skel skel--stat" />
          ))}
        </div>
        <div className="skel skel--card" />
      </>
    );
  }

  return (
    <>
      <SoortTegels
        tegels={tegels}
        gekozen={soort}
        onKies={setSoort}
        alleLabel={k("soort.alle")}
        totaal={resultaten.length}
      />

      <div className="da-overview__filters">
        <div className="da-overview__filter-grid">
          <div className="fawkes-products-search da-overview__search">
            <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              autoComplete="off"
              className="fawkes-products-search__input"
              placeholder={k("filter.zoek")}
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              aria-label={k("filter.zoek")}
            />
          </div>

          <label className="da-overview__field">
            <span className="da-overview__field-label">{k("filter.prioriteit")}</span>
            <select className="da-overview__filter" value={prioriteit} onChange={(e) => setPrioriteit(e.target.value)}>
              <option value="">{k("filter.alle")}</option>
              <option value="rood">{k("prio.rood")}</option>
              <option value="oranje">{k("prio.oranje")}</option>
              <option value="groen">{k("prio.groen")}</option>
            </select>
          </label>

          <label className="da-overview__field">
            <span className="da-overview__field-label">{k("filter.moment")}</span>
            <select className="da-overview__filter" value={moment} onChange={(e) => setMoment(e.target.value)}>
              <option value="">{k("filter.alle")}</option>
              {CONTACTMOMENTEN.map((cm) => (
                <option key={cm} value={cm}>{k(`moment.${cm}`)}</option>
              ))}
            </select>
          </label>

          <label className="da-overview__field">
            <span className="da-overview__field-label">{k("filter.status")}</span>
            <select className="da-overview__filter" value={afhandeling} onChange={(e) => setAfhandeling(e.target.value)}>
              <option value="">{k("filter.alle")}</option>
              <option value="open">{k("filter.onbehandeld")}</option>
              <option value="opgepakt">{k("filter.opgepakt")}</option>
            </select>
          </label>

          <div className="da-overview__field da-overview__field--action">
            <span className="da-overview__field-label" aria-hidden="true">&nbsp;</span>
            <button type="button" className="da-overview__wis" onClick={wis} disabled={!filterActief}>
              <span className="da-overview__wis-icon" aria-hidden="true">↻</span>
              {k("filter.wissen")}
            </button>
          </div>
        </div>
      </div>

      <div className="da-overview__resultsbar">
        <span className="da-overview__results-count">
          <PawIcon width={16} height={16} />{" "}
          {(zichtbaar.length === 1 ? k("resultaten.aantalEen") : k("resultaten.aantal"))
            .replace("%d", String(zichtbaar.length))}
        </span>
      </div>

      {zichtbaar.length === 0 ? (
        <div className="card">
          <div className="empty">{filterActief ? k("resultaten.leegFilter") : k("aandacht.empty")}</div>
        </div>
      ) : (
        <ResultaatGrid resultaten={zichtbaar} k={k} onOpenInzending={onOpenInzending} />
      )}
    </>
  );
}

/**
 * "Nazorg-resultaten": the foto-card grid the Jmix screen shows (`NazorgTegel`) — the animal's picture
 * with its priority badge, then who adopted it, which contact moment it answered, how long ago it came
 * in, and how many flag rules fired. As a name-and-badge row it told an operator nothing to act on.
 *
 * Its own component so the photo hook is called once for the whole grid, above the tab's early returns.
 * Photos ride the authenticated blob fetch — a plain <img src> can't carry the bearer, and the feed's
 * `fotoUrl` is only ever set for animals with an ONLINE image (see useAnimalPhotoUrls).
 */
function ResultaatGrid({
  resultaten,
  k,
  onOpenInzending,
}: {
  resultaten: Resultaat[];
  k: TFn;
  onOpenInzending: (inzendingId: number | null) => void;
}) {
  const fotos = useAnimalPhotoUrls(resultaten.map((a) => a.dierId).filter((id): id is number => id != null));
  return (
    <div className="aandacht">
      {resultaten.map((a, i) => {
        const foto = a.dierId != null ? fotos[String(a.dierId)] ?? a.fotoUrl : a.fotoUrl;
        return (
          <div
            // A dier can surface more than once in the attention list, so dierId alone isn't unique —
            // pair it with the index to avoid duplicate React keys (dropped/duplicated cards).
            key={`${a.dierId ?? "x"}-${i}`}
            className={"aandacht__kaart aandacht__kaart--" + (a.prioriteit || "groen")}
            role="button"
            tabIndex={0}
            onClick={() => onOpenInzending(a.inzendingId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenInzending(a.inzendingId);
              }
            }}
          >
            <div className="aandacht__foto">
              {foto ? (
                <img src={foto} alt="" />
              ) : (
                <span className={"avatar avatar--" + (a.prioriteit || "groen")}>{initial(a.naam)}</span>
              )}
              <span className="aandacht__badge"><PrioPill k={k} prioriteit={a.prioriteit} /></span>
              {a.opgepakt && <span className="aandacht__opgepakt">{k("filter.opgepakt")}</span>}
            </div>
            <div className="aandacht__body">
              <div className="aandacht__naam">{a.naam || "—"}</div>
              <div className="aandacht__soort">{a.diersoort || "—"}</div>
              <dl className="aandacht__regels">
                <dt>{k("aandacht.adoptant")}</dt>
                <dd>{a.adoptant || "—"}</dd>
                <dt>{k("aandacht.vragenlijst")}</dt>
                <dd>{a.vragenlijst || "—"}</dd>
                <dt>{k("aandacht.moment")}</dt>
                {/* Het hoeveelste nazorgmoment bij dit dier. Stond hier het contactmoment uit de
                    uitnodiging, maar die bestaat niet als de nazorg via het platform is verstuurd —
                    dan bleef er een streepje staan. */}
                <dd>{a.momentNummer != null ? `${a.momentNummer}e` : "—"}</dd>
                <dt>{k("aandacht.ingediend")}</dt>
                <dd>{a.geleden || "—"}</dd>
                <dt>{k("aandacht.resultaat")}</dt>
                <dd className={"aandacht__signalen aandacht__signalen--" + (a.prioriteit || "groen")}>
                  {(a.signalen === 1 ? k("aandacht.signaal") : k("aandacht.signalen"))
                    .replace("%d", String(a.signalen))}
                </dd>
              </dl>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ k, status }: { k: TFn; status: string | null }) {
  if (status === "GEPUBLICEERD") return <span className="pill pill--green">{k("status.gepubliceerd")}</span>;
  if (status === "CONCEPT") return <span className="pill pill--orange">{k("status.concept")}</span>;
  if (status === "GEARCHIVEERD") return <span className="pill">{k("status.gearchiveerd")}</span>;
  return <span className="pill">{status || "—"}</span>;
}

const VERZEND_FILTERS = ["alles", "gepland", "verstuurd"] as const;
type VerzendFilter = (typeof VERZEND_FILTERS)[number];

// ── Tab 2: Taken — de verzendlijst. Zo gevraagd: alles wat verstuurd gaat worden of verstuurd is, met
// de naam van de vragenlijst erbij en de hoeveelste keer dit dier benaderd wordt. De oude tab liet
// alleen rijen "Nazorg-uitnodiging versturen" zien: elf keer dezelfde titel, zonder dier of lijst.
function VerzendingenTab({
  k,
  onOpenInzending,
}: {
  k: TFn;
  onOpenInzending: (inzendingId: number | null) => void;
}) {
  const locale = useGetLocale()() ?? "nl";
  const [filter, setFilter] = useState<VerzendFilter>("alles");
  // Zoeken en de periode gaan naar de api: de lijst is afgekapt, dus hier filteren zou oudere
  // verzendingen stilletjes buiten beeld houden.
  const [zoek, setZoek] = useState("");
  const [van, setVan] = useState("");
  const [tot, setTot] = useState("");
  const vraag = new URLSearchParams();
  if (van) vraag.set("van", van);
  if (tot) vraag.set("tot", tot);
  if (zoek.trim()) vraag.set("zoek", zoek.trim());
  const url = `nazorg/beheer/verzendingen${vraag.size > 0 ? `?${vraag}` : ""}`;
  const { data, isLoading } = useCustom<Verzending[]>({ url, method: "get" });
  const rijen = useMemo(() => data?.data ?? [], [data]);
  const loading = isLoading || !data?.data;

  const zichtbaar = useMemo(
    () =>
      rijen.filter((r) => {
        if (filter === "gepland") return r.status === "GEPLAND";
        if (filter === "verstuurd") return r.status !== "GEPLAND";
        return true;
      }),
    [rijen, filter],
  );

  const statusPill = (status: string): string => {
    if (status === "GEPLAND") return "pill pill--orange";
    if (status === "INGEVULD") return "pill pill--green";
    if (status === "VERLOPEN") return "pill";
    return "pill pill--green";
  };
  const statusLabel = (status: string): string => {
    const label = k(`verzending.status.${status}`);
    return label === `nazorg.verzending.status.${status}` ? status : label;
  };

  return (
    <>
      <div className="nz__head-row">
        <div>
          <h2 className="nz__title">{k("verzending.title")}</h2>
          <p className="nz__subtitle">{k("verzending.subtitle")}</p>
        </div>
      </div>

      {/* Eén balk: waar je op filtert (de pillen), waar je op zoekt en over welke periode. */}
      <div className="nz__balk">
        <div className="filterpills">
          {VERZEND_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={"filterpill" + (filter === f ? " is-active" : "")}
              onClick={() => setFilter(f)}
            >
              {k(`verzending.filter.${f}`)}
            </button>
          ))}
        </div>

        <div className="nz__zoekrij">
          <span className="nz__zoek">
            <SearchIcon width={15} height={15} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              placeholder={k("verzending.zoek")}
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              aria-label={k("verzending.zoek")}
            />
          </span>
          <span className="nz__periode">
            <span className="nz__periode-label">{k("verzending.periode")}</span>
            <input
              type="date"
              value={van}
              onChange={(e) => setVan(e.target.value)}
              aria-label={k("verzending.van")}
            />
            <span className="nz__periode-streep" aria-hidden="true">–</span>
            <input
              type="date"
              value={tot}
              min={van || undefined}
              onChange={(e) => setTot(e.target.value)}
              aria-label={k("verzending.tot")}
            />
          </span>
          {(zoek || van || tot) && (
            <button
              type="button"
              className="nz__wis"
              onClick={() => {
                setZoek("");
                setVan("");
                setTot("");
              }}
            >
              {k("filter.wissen")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card tablecard" style={{ padding: 22 }}>
          <div className="skel skel--line" style={{ width: "60%" }} />
          <div className="skel skel--line" style={{ width: "80%" }} />
          <div className="skel skel--line" style={{ width: "70%" }} />
        </div>
      ) : zichtbaar.length === 0 ? (
        <div className="card">
          <div className="empty">{k("verzending.empty")}</div>
        </div>
      ) : (
        <div className="card tablecard">
          <IndeelbareTabel
            tabelKey="nazorg-verzendingen"
            columns={[
              { key: "dier", header: k("verzending.col.dier"),
                cell: (r) => <span className="col-naam">{r.dier || "—"}</span> },
              { key: "vragenlijst", header: k("verzending.col.vragenlijst"), cell: (r) => r.vragenlijst || "—" },
              { key: "moment", header: k("verzending.col.moment"),
                cell: (r) => r.contactmomentLabel || r.contactmoment || "—" },
              { key: "keer", header: k("verzending.col.keer"),
                cell: (r) => k("verzending.keer").replace("%d", String(r.volgnummer)) },
              {
                key: "adoptant", header: k("verzending.col.adoptant"),
                cell: (r) => (
                  <>
                    {r.adoptant || "—"}
                    {r.adoptantPlaats && <div className="nz__subtekst">{r.adoptantPlaats}</div>}
                  </>
                ),
              },
              { key: "datum", header: k("verzending.col.datum"), cell: (r) => formatDate(r.datum, locale) },
              { key: "status", header: k("verzending.col.status"),
                cell: (r) => <span className={statusPill(r.status)}>{statusLabel(r.status)}</span> },
            ]}
            rows={zichtbaar}
            getRowKey={(r) => `${r.uitnodigingId ?? "t"}-${r.dierId ?? "x"}-${r.volgnummer}`}
            // Doorklikken opent de nazorg, nooit het dierscherm: de inzending bij deze uitnodiging, en
            // is die er nog niet, dan de laatste nazorg van dit dier.
            onRowClick={(r) => onOpenInzending(r.dossierInzendingId)}
            rowClassName={(r) => (r.dossierInzendingId != null ? "rij-klikbaar" : undefined)}
            empty=""
          />
        </div>
      )}
      <p className="nz__voetnoot">{k("verzending.toelichting")}</p>
    </>
  );
}

// ── Tab 3: Vragenlijsten — the aftercare questionnaire overview with in-SPA authoring
// (nazorg-authoring epic): "Nieuwe nazorg-vragenlijst" and row-clicks navigate to the settings
// wizard-builder (Instellingen → Registraties & Wizards), replacing the old dead hand-offs to the
// Jmix wizard-builder. De diersoortkeuze is dezelfde tegelrij als op Aanwezig.
function VragenlijstenTab({
  k,
  loading,
  data,
  onNieuw,
  onOpen,
}: {
  k: TFn;
  loading: boolean;
  data: NazorgPayload | null;
  onNieuw: () => void;
  onOpen: (definitionId: string) => void;
}) {
  const [soort, setSoort] = useState<string | null>(null);
  const lijsten = useMemo(() => data?.vragenlijsten ?? [], [data]);
  // Een lijst zonder diersoort geldt voor alle soorten en telt dus bij elke tegel mee.
  const hoortBij = (v: Vragenlijst, s: string) => {
    const ds = (v.diersoort || "").toLowerCase();
    return ds === s.toLowerCase() || ds === "alle" || ds === "";
  };
  const tegels = useMemo(
    () => (data?.diersoorten ?? [])
      .map((s) => ({ naam: s, aantal: lijsten.filter((v) => hoortBij(v, s)).length }))
      .filter((tegel) => tegel.aantal > 0),
    [data, lijsten],
  );
  const zichtbaar = useMemo(
    () => (soort ? lijsten.filter((v) => hoortBij(v, soort)) : lijsten),
    [lijsten, soort],
  );

  return (
    <>
      <div className="nz__head-row">
        <div>
          <h2 className="nz__title">{k("vragenlijsten.title")}</h2>
          <p className="nz__subtitle">{k("vragenlijsten.subtitle")}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={onNieuw}>
          {k("vragenlijsten.nieuweLijst")}
        </button>
      </div>

      {tegels.length > 0 && (
        <SoortTegels
          tegels={tegels}
          gekozen={soort}
          onKies={setSoort}
          alleLabel={k("vragenlijsten.filterAll")}
          totaal={lijsten.length}
        />
      )}

      {loading ? (
        <div className="card tablecard" style={{ padding: 22 }}>
          <div className="skel skel--line" style={{ width: "60%" }} />
          <div className="skel skel--line" style={{ width: "80%" }} />
          <div className="skel skel--line" style={{ width: "70%" }} />
        </div>
      ) : zichtbaar.length === 0 ? (
        <div className="card">
          <div className="empty">{k("vragenlijsten.empty")}</div>
        </div>
      ) : (
        <div className="card tablecard">
          <IndeelbareTabel
            tabelKey="nazorg-vragenlijsten"
            columns={[
              { key: "naam", header: k("vragenlijsten.col.naam"),
                cell: (v) => <span className="col-naam">{v.naam || "—"}</span> },
              { key: "diersoort", header: k("vragenlijsten.col.diersoort"), cell: (v) => v.diersoort || "—" },
              { key: "contactmoment", header: k("vragenlijsten.col.contactmoment"),
                cell: (v) => v.contactmomentLabel || v.contactmoment || "—" },
              { key: "kanaal", header: k("vragenlijsten.col.kanaal"),
                cell: () => <span className="pill pill--green">{k("vragenlijsten.kanaalPlatform")}</span> },
              { key: "status", header: k("vragenlijsten.col.status"),
                cell: (v) => <StatusPill k={k} status={v.status} /> },
            ]}
            rows={zichtbaar}
            getRowKey={(v) => String(v.id)}
            onRowClick={(v) => onOpen(v.id)}
            rowClassName={() => "rij-klikbaar"}
            empty=""
          />
        </div>
      )}
    </>
  );
}
